/**
 * OsirisBrain ERP — useBusinessConfig hook
 *
 * Provides the active business type configuration to any component.
 * This is the single source of truth for what the app shows.
 */

import { useState, useEffect } from "react";
import { useTenant } from "../lib/TenantContext";
import { supabase } from "../lib/supabase";
import {
  getBusinessConfigSafe,
  type BusinessTypeConfig,
} from "../config/businessTypes";

export function useBusinessConfig() {
  const { tenantId } = useTenant();
  const [config, setConfig] = useState<BusinessTypeConfig>(
    getBusinessConfigSafe("general"),
  );
  const [businessTypeCode, setBusinessTypeCode] = useState<string>("general");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;

    const loadConfig = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("tenants")
        .select("business_type")
        .eq("id", tenantId)
        .single();

      const code = data?.business_type || "general";
      setBusinessTypeCode(code);
      setConfig(getBusinessConfigSafe(code));
      setLoading(false);
    };

    loadConfig();

    // Listen for changes to the tenant's business_type
    const channel = supabase
      .channel("tenant-business-type")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "tenants",
          filter: `id=eq.${tenantId}`,
        },
        (payload) => {
          const newCode = payload.new?.business_type || "general";
          setBusinessTypeCode(newCode);
          setConfig(getBusinessConfigSafe(newCode));
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tenantId]);

  return {
    config,
    businessTypeCode,
    loading,
    labels: config.labels,
    modules: config.modules,
    employeeTypes: config.employeeTypes,
    sidebar: config.sidebar,
    productFields: config.productFields,
    pos: config.pos,
    isModuleEnabled: (module: keyof BusinessTypeConfig["modules"]) =>
      config.modules[module] ?? false,
  };
}
